import * as THREE from 'three';
import type { QualitySettings } from './Renderer';

/** Une ambiance = réglage complet de la lumière et du fond, interpolable. */
export interface Ambiance {
  label: string;
  bg: number;
  key: { color: number; intensity: number; pos: [number, number, number] };
  rimL: { color: number; intensity: number };
  rimR: { color: number; intensity: number };
  fill: { sky: number; ground: number; intensity: number };
  aura: [number, number];
  env: number;
  fog: number;
}

export const AMBIANCES: Record<string, Ambiance> = {
  studio: {
    label: 'Studio', bg: 0x0d090b, key: { color: 0xffeee4, intensity: 20, pos: [-1.6, 3.1, 2.6] },
    rimL: { color: 0xb8b4ff, intensity: 26 }, rimR: { color: 0xffb3c4, intensity: 30 },
    fill: { sky: 0xffe9e2, ground: 0x1a1012, intensity: 0.22 }, aura: [0x2e1219, 0x16112c], env: 0.38, fog: 0.085,
  },
  crepuscule: {
    label: 'Crépuscule', bg: 0x140a06, key: { color: 0xffa865, intensity: 26, pos: [-2.6, 1.9, 1.8] },
    rimL: { color: 0x7f8cff, intensity: 18 }, rimR: { color: 0xff8a4a, intensity: 38 },
    fill: { sky: 0xffd2a8, ground: 0x1c0f08, intensity: 0.26 }, aura: [0x3a1a08, 0x1a0c22], env: 0.34, fog: 0.07,
  },
  nuit: {
    label: 'Nuit', bg: 0x05070d, key: { color: 0xb9c8ff, intensity: 11, pos: [1.4, 3.4, 2.2] },
    rimL: { color: 0x5d7bff, intensity: 34 }, rimR: { color: 0xc77dff, intensity: 22 },
    fill: { sky: 0x8fa0d8, ground: 0x07080e, intensity: 0.14 }, aura: [0x0b1430, 0x1d0b2a], env: 0.22, fog: 0.1,
  },
  neon: {
    label: 'Néon', bg: 0x07040a, key: { color: 0xffd6f0, intensity: 9, pos: [-0.8, 3.0, 2.8] },
    rimL: { color: 0x2fe6ff, intensity: 55 }, rimR: { color: 0xff2fa8, intensity: 60 },
    fill: { sky: 0xd0a0ff, ground: 0x0a0410, intensity: 0.12 }, aura: [0x3a0830, 0x062a36], env: 0.25, fog: 0.09,
  },
};

/**
 * Le plateau : studio photo intimiste — fond infini sombre et chaud, éclairage "beauté"
 * (key douce + deux contre-jours colorés), environnement HDR procédural pour les reflets
 * (les "catchlights" dans les yeux sont essentiels à la sensation de présence).
 */
export class Stage {
  readonly scene = new THREE.Scene();
  readonly key: THREE.SpotLight;
  readonly rimLeft: THREE.SpotLight;
  readonly rimRight: THREE.SpotLight;
  readonly fill: THREE.HemisphereLight;
  readonly floor: THREE.Mesh;
  readonly aura: THREE.Mesh;
  private dust?: THREE.Points;
  private envRT?: THREE.WebGLRenderTarget;
  private t = 0;
  private amb?: { from: Ambiance; to: Ambiance; t: number; dur: number };
  private current: Ambiance = AMBIANCES.studio;

  static readonly BG = new THREE.Color(0x0d090b);

  constructor(private renderer: THREE.WebGLRenderer, quality: QualitySettings) {
    const s = this.scene;
    s.background = Stage.BG.clone();
    s.fog = new THREE.FogExp2(Stage.BG.getHex(), 0.085);

    // ---------- Environnement HDR procédural (softboxes) ----------
    this.buildEnvironment();

    // ---------- Lumières ----------
    // Key : grande softbox chaude, avant-gauche, en hauteur.
    this.key = new THREE.SpotLight(0xffeee4, 20, 0, THREE.MathUtils.degToRad(26), 0.85, 2);
    this.key.position.set(-1.6, 3.1, 2.6);
    this.key.target.position.set(0, 1.25, 0);
    this.key.castShadow = true;
    this.key.shadow.mapSize.setScalar(quality.shadowMapSize);
    this.key.shadow.radius = quality.shadowRadius;
    this.key.shadow.blurSamples = 16;
    this.key.shadow.bias = -0.00015;
    this.key.shadow.normalBias = 0.012;
    this.key.shadow.camera.near = 1.5;
    this.key.shadow.camera.far = 8;
    s.add(this.key, this.key.target);

    // Contre-jours : lavande froide à gauche, rose chaud à droite → silhouette détachée du fond.
    this.rimLeft = new THREE.SpotLight(0xb8b4ff, 26, 0, THREE.MathUtils.degToRad(22), 0.9, 2);
    this.rimLeft.position.set(-2.2, 2.6, -2.4);
    this.rimLeft.target.position.set(0, 1.35, 0);
    this.rimRight = new THREE.SpotLight(0xffb3c4, 30, 0, THREE.MathUtils.degToRad(22), 0.9, 2);
    this.rimRight.position.set(2.3, 2.2, -2.1);
    this.rimRight.target.position.set(0, 1.25, 0);
    s.add(this.rimLeft, this.rimLeft.target, this.rimRight, this.rimRight.target);

    // Débouchage très doux (ciel chaud / sol plus sombre).
    this.fill = new THREE.HemisphereLight(0xffe9e2, 0x1a1012, 0.22);
    s.add(this.fill);

    // ---------- Sol infini ----------
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x1a1214, roughness: 0.62, metalness: 0.0 });
    // Fondu radial du sol dans le fond → pas d'horizon visible.
    floorMat.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWorldPosF;')
        .replace('#include <dithering_fragment>', `#include <dithering_fragment>
          float r = length(vWorldPosF.xz);
          float k = smoothstep(1.2, 6.5, r);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, k);`);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWorldPosF;')
        .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWorldPosF = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    };
    this.floor = new THREE.Mesh(new THREE.CircleGeometry(14, 96).rotateX(-Math.PI / 2), floorMat);
    this.floor.receiveShadow = true;
    s.add(this.floor);

    // ---------- Aura de fond (dégradé chaud derrière elle) ----------
    const auraMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, fog: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uColor: { value: new THREE.Color(0x2e1219) }, uColor2: { value: new THREE.Color(0x16112c) }, uTime: { value: 0 } },
      vertexShader: /* glsl */`varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragmentShader: /* glsl */`
        varying vec2 vUv; uniform vec3 uColor; uniform vec3 uColor2; uniform float uTime;
        void main(){
          vec2 p = vUv - vec2(0.5, 0.48);
          float d = length(p * vec2(1.0, 0.8));
          float a = exp(-d * d * 14.0);
          float b = exp(-dot(p - vec2(0.22, 0.12), p - vec2(0.22, 0.12)) * 14.0);
          vec3 c = uColor * a * (0.9 + 0.1 * sin(uTime * 0.21)) + uColor2 * b * 0.6;
          gl_FragColor = vec4(c, 1.0);
        }`,
    });
    this.aura = new THREE.Mesh(new THREE.PlaneGeometry(9, 7), auraMat);
    this.aura.position.set(0, 1.9, -3.2);
    s.add(this.aura);

    if (quality.dust) this.buildDust();
  }

  private buildEnvironment() {
    // Petite "pièce" virtuelle : murs sombres + softboxes émissives HDR.
    const env = new THREE.Scene();
    env.background = new THREE.Color(0x0a0708);
    const room = new THREE.Mesh(new THREE.BoxGeometry(12, 8, 12), new THREE.MeshBasicMaterial({ color: 0x120c0e, side: THREE.BackSide }));
    room.position.y = 3;
    env.add(room);
    const box = (w: number, h: number, color: number, intensity: number, pos: [number, number, number], look: [number, number, number]) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity), side: THREE.DoubleSide }));
      m.position.set(...pos);
      m.lookAt(...look);
      env.add(m);
    };
    box(2.6, 1.8, 0xfff1e6, 5.5, [-1.8, 3.2, 3.2], [0, 1.4, 0]);   // key softbox → catchlight principal
    box(1.2, 3.2, 0xffd6e0, 2.2, [3.4, 2.0, -1.5], [0, 1.4, 0]);   // strip rose
    box(1.2, 3.2, 0xcfd0ff, 2.0, [-3.4, 2.0, -1.8], [0, 1.4, 0]);  // strip lavande
    box(4.0, 1.0, 0xffe6d6, 0.9, [0, 5.5, 0.5], [0, 0, 0]);        // plafond
    box(6.0, 6.0, 0x2a1a1c, 0.6, [0, -0.5, 0], [0, 5, 0]);         // rebond sol chaud
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.envRT = pmrem.fromScene(env, 0.03);
    pmrem.dispose();
    // La scène d'environnement ne sert qu'au précalcul : on libère tout de suite ses ressources.
    env.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
    });
    this.scene.environment = this.envRT.texture;
    this.scene.environmentIntensity = this.current?.env ?? 0.38;
  }

  private buildDust() {
    // Poussière en suspension dans la lumière : quelques centaines de points, coût négligeable.
    const N = 420;
    const pos = new Float32Array(N * 3);
    const seed = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 4.5;
      pos[i * 3 + 1] = Math.random() * 3.2;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 3.5 - 0.3;
      seed[i] = Math.random();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('seed', new THREE.BufferAttribute(seed, 1));
    const m = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uPx: { value: 1 } },
      vertexShader: /* glsl */`
        attribute float seed; uniform float uTime; uniform float uPx; varying float vA;
        void main(){
          vec3 p = position;
          float t = uTime * (0.02 + seed * 0.03);
          p.x += sin(t * 6.283 + seed * 40.0) * 0.25;
          p.y = mod(p.y + t * 0.6, 3.2);
          p.z += cos(t * 4.0 + seed * 17.0) * 0.2;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          float size = (0.6 + seed * 1.4) * uPx;
          gl_PointSize = size * 9.0 / -mv.z;
          // Visible surtout dans le cône de la key (avant-gauche) et pas trop près de la caméra.
          float beam = smoothstep(2.2, 0.2, length(p.xz - vec2(-0.6, 0.9)));
          vA = (0.15 + 0.85 * beam) * smoothstep(0.4, 1.4, -mv.z) * (0.4 + 0.6 * fract(seed * 7.3));
        }`,
      fragmentShader: /* glsl */`
        varying float vA;
        void main(){
          vec2 c = gl_PointCoord - 0.5;
          float a = exp(-dot(c, c) * 18.0) * vA * 0.55;
          gl_FragColor = vec4(vec3(1.0, 0.9, 0.82) * a, 1.0);
        }`,
    });
    this.dust = new THREE.Points(g, m);
    this.dust.frustumCulled = false;
    this.scene.add(this.dust);
  }

  setDust(on: boolean) {
    if (on && !this.dust) this.buildDust();
    if (this.dust) this.dust.visible = on;
  }

  setPixelRatio(px: number) {
    if (this.dust) (this.dust.material as THREE.ShaderMaterial).uniforms.uPx.value = px;
  }

  /** Ajuste le cadrage des ombres autour du personnage (hauteur en mètres). */
  frameShadows(height: number) {
    const cam = this.key.shadow.camera as THREE.PerspectiveCamera;
    cam.near = 1.5; cam.far = 8;
    this.key.angle = THREE.MathUtils.degToRad(height > 1.2 ? 26 : 20);
    cam.updateProjectionMatrix();
  }

  /** Passe à une autre ambiance en fondu (couleurs, intensités, position de la key, fond). */
  setAmbiance(id: string, duration = 1.6) {
    const to = AMBIANCES[id];
    if (!to) return;
    this.setAmbianceDef(to, duration);
    this.onAmbiance(id);
  }

  /** Notifié quand une ambiance nommée est choisie (HUD, scénarios…). */
  onAmbiance: (id: string) => void = () => {};

  /** Variante avec une ambiance fournie (ex. par un décor). duration = 0 : immédiat. */
  setAmbianceDef(to: Ambiance, duration = 1.6) {
    if (duration <= 0) { this.applyAmbiance(to, to, 1); this.current = to; this.amb = undefined; return; }
    this.amb = { from: this.snapshot(), to, t: 0, dur: duration };
  }

  get ambiance(): Ambiance { return this.current; }

  /** Affiche/masque le sol infini et l'aura du studio (un décor peut fournir les siens). */
  setStudioVisible(v: boolean) {
    this.floor.visible = v;
    this.aura.visible = v;
  }

  /**
   * Reflets (IBL) : précalcule l'environnement depuis une scène fournie par un décor,
   * ou revient aux softboxes du studio si `env` est null.
   */
  setEnvironmentFrom(env: THREE.Scene | null) {
    if (!env) {
      this.envRT?.dispose();
      this.buildEnvironment();
      return;
    }
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const rt = pmrem.fromScene(env, 0.03);
    pmrem.dispose();
    this.envRT?.dispose();
    this.envRT = rt;
    this.scene.environment = rt.texture;
  }

  private snapshot(): Ambiance {
    const c = (x: THREE.Color) => x.getHex();
    const aura = (this.aura.material as THREE.ShaderMaterial).uniforms;
    return {
      label: this.current.label, bg: c(this.scene.background as THREE.Color),
      key: { color: c(this.key.color), intensity: this.key.intensity, pos: this.key.position.toArray() as [number, number, number] },
      rimL: { color: c(this.rimLeft.color), intensity: this.rimLeft.intensity },
      rimR: { color: c(this.rimRight.color), intensity: this.rimRight.intensity },
      fill: { sky: c(this.fill.color), ground: c(this.fill.groundColor), intensity: this.fill.intensity },
      aura: [c(aura.uColor.value), c(aura.uColor2.value)], env: this.scene.environmentIntensity, fog: (this.scene.fog as THREE.FogExp2).density,
    };
  }

  private applyAmbiance(a: Ambiance, b: Ambiance, u: number) {
    const col = (out: THREE.Color, x: number, y: number) => out.setHex(x).lerp(_c.setHex(y), u);
    const lerp = THREE.MathUtils.lerp;
    col(this.scene.background as THREE.Color, a.bg, b.bg);
    (this.scene.fog as THREE.FogExp2).color.copy(this.scene.background as THREE.Color);
    (this.scene.fog as THREE.FogExp2).density = lerp(a.fog, b.fog, u);
    col(this.key.color, a.key.color, b.key.color);
    this.key.intensity = lerp(a.key.intensity, b.key.intensity, u);
    this.key.position.fromArray(a.key.pos).lerp(_v.fromArray(b.key.pos), u);
    col(this.rimLeft.color, a.rimL.color, b.rimL.color);
    this.rimLeft.intensity = lerp(a.rimL.intensity, b.rimL.intensity, u);
    col(this.rimRight.color, a.rimR.color, b.rimR.color);
    this.rimRight.intensity = lerp(a.rimR.intensity, b.rimR.intensity, u);
    col(this.fill.color, a.fill.sky, b.fill.sky);
    col(this.fill.groundColor, a.fill.ground, b.fill.ground);
    this.fill.intensity = lerp(a.fill.intensity, b.fill.intensity, u);
    const au = (this.aura.material as THREE.ShaderMaterial).uniforms;
    col(au.uColor.value, a.aura[0], b.aura[0]);
    col(au.uColor2.value, a.aura[1], b.aura[1]);
    this.scene.environmentIntensity = lerp(a.env, b.env, u);
  }

  update(dt: number) {
    this.t += dt;
    if (this.amb) {
      const a = this.amb;
      a.t += dt;
      const u = Math.min(1, a.t / a.dur);
      this.applyAmbiance(a.from, a.to, u * u * (3 - 2 * u));
      if (u >= 1) { this.current = a.to; this.amb = undefined; }
    }
    (this.aura.material as THREE.ShaderMaterial).uniforms.uTime.value = this.t;
    if (this.dust) (this.dust.material as THREE.ShaderMaterial).uniforms.uTime.value = this.t;
  }

  dispose() {
    this.envRT?.dispose();
  }
}

const _c = new THREE.Color();
const _v = new THREE.Vector3();
